import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Activity, ArrowLeft, Download, Info, AlertCircle, Pill, TestTube, Heart, Leaf } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DiseaseProfile = () => {
  const { name } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [disease, setDisease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (location.state?.data) {
      setDisease(location.state.data);
      setLoading(false);
    } else {
      // Fetch disease data if not in state
      fetchDiseaseData();
    }
  }, [location.state]);

  const fetchDiseaseData = async () => {
    try {
      const mode = localStorage.getItem('medplexity_mode') || 'patient';
      const response = await axios.post(`${API}/search/disease`, {
        query: name,
        mode: mode
      }, {
        withCredentials: true
      });
      setDisease(response.data);
    } catch (error) {
      console.error('Error fetching disease:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!disease) return;
    
    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/report/generate?disease_id=${disease.id}`, {}, {
        withCredentials: true
      });
      setReport(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!disease) {
    return <div>Disease not found</div>;
  }

  const renderTreatments = () => {
    if (!disease.treatments || Object.keys(disease.treatments).length === 0) {
      return <p className="profile-section-content">Treatment information not available.</p>;
    }

    return (
      <div className="treatment-grid">
        {Object.entries(disease.treatments).map(([pathy, details]) => (
          <div key={pathy} className="treatment-card" data-testid={`treatment-${pathy}`}>
            <div className="treatment-pathy">
              {pathy.replace('_', ' ').charAt(0).toUpperCase() + pathy.replace('_', ' ').slice(1)}
            </div>
            {typeof details === 'object' ? (
              Object.entries(details).map(([type, info]) => (
                <div key={type} className="treatment-detail">
                  <strong>{type.replace('_', ' ')}:</strong> {info}
                </div>
              ))
            ) : (
              <div className="treatment-detail">{details}</div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="disease-profile-container" data-testid="disease-profile-page">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-brand">
          <Activity size={28} />
          MedPlexity
        </div>
        <div className="navbar-menu">
          <button 
            className="nav-link" 
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={18} /> Back
          </button>
        </div>
      </nav>

      {/* Disease Header */}
      <div className="disease-header">
        <h1 className="disease-name" data-testid="disease-name">{name}</h1>
        <button 
          className="action-btn btn-primary" 
          onClick={generateReport}
          disabled={generatingReport}
          data-testid="generate-report-btn"
        >
          <Download size={18} /> {generatingReport ? 'Generating...' : 'Generate AI Report'}
        </button>
      </div>

      {/* Report Display */}
      {report && (
        <div className="disease-content">
          <div className="profile-card" data-testid="ai-report">
            <h2 className="profile-section-title">
              <Info /> AI Generated Summary Report
            </h2>
            <div className="profile-section-content">
              {report.summary}
            </div>
          </div>
        </div>
      )}

      {/* Disease Content */}
      <div className="disease-content">
        <div className="profile-card">
          <h2 className="profile-section-title" data-testid="section-definition">
            <Info /> Definition
          </h2>
          <p className="profile-section-content">{disease.definition}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title" data-testid="section-etiology">
            <AlertCircle /> Etiology (Causes)
          </h2>
          <p className="profile-section-content">{disease.etiology}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title">
            <Activity /> Age Groups Affected
          </h2>
          <p className="profile-section-content">{disease.age_groups}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title">
            <Activity /> Types
          </h2>
          <p className="profile-section-content">{disease.types}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title">
            <Activity /> Stages
          </h2>
          <p className="profile-section-content">{disease.stages}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title">
            <AlertCircle /> Prodromal Symptoms (Early Signs)
          </h2>
          <p className="profile-section-content">{disease.prodromal_symptoms}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title" data-testid="section-symptoms">
            <Heart /> Symptoms
          </h2>
          <p className="profile-section-content">{disease.symptoms}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title">
            <Activity /> Pathogenesis (Disease Mechanism)
          </h2>
          <p className="profile-section-content">{disease.pathogenesis}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title">
            <AlertCircle /> Complications
          </h2>
          <p className="profile-section-content">{disease.complications}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title">
            <TestTube /> Investigations (Diagnostic Tests)
          </h2>
          <p className="profile-section-content">{disease.investigations}</p>
        </div>

        <div className="profile-card">
          <h2 className="profile-section-title" data-testid="section-treatments">
            <Pill /> Treatment (Multi-Pathy Approach)
          </h2>
          {renderTreatments()}
        </div>
      </div>
    </div>
  );
};

export default DiseaseProfile;
